import { AppLink, Button, Callout, Field, Input } from "@nodus/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import * as React from "react";
import { AuthCard, rememberRedirect, safeRedirect } from "../../../app/auth-card";
import { useCaptcha } from "../../../app/turnstile";
import { useAuth } from "../../../auth/auth-context";

export const Route = createFileRoute("/sign-in")({
  validateSearch: (s: Record<string, unknown>): { redirect?: string; notice?: string } => ({
    ...(typeof s.redirect === "string" ? { redirect: s.redirect } : {}),
    ...(typeof s.notice === "string" ? { notice: s.notice } : {}),
  }),
  component: SignIn,
});

function SignIn() {
  const { redirect, notice } = Route.useSearch();
  const { auth } = useAuth();
  const navigate = useNavigate();
  const providers = useQuery({ queryKey: ["auth", "providers"], queryFn: () => auth.providers(), staleTime: Infinity });
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string>();
  const [busy, setBusy] = React.useState(false);
  const captcha = useCaptcha();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await auth.signInWithPassword(email.trim(), password, captcha.token);
      void navigate({ href: safeRedirect(redirect) });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed.");
    } finally {
      setBusy(false);
      captcha.reset();
    }
  }

  async function oauth(provider: "github" | "google") {
    rememberRedirect(redirect);
    try {
      await auth.signInWithOAuth(provider, `${window.location.origin}/auth/callback`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed.");
    }
  }

  return (
    <AuthCard
      title="Sign in to Nodus"
      footer={
        <>
          New to Nodus?{" "}
          <AppLink href={`/sign-up${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ""}`} className="font-medium text-fg-primary underline">
            Create an account
          </AppLink>
        </>
      }
    >
      {notice === "signed-out" ? <Callout tone="neutral" className="mb-4">You signed out.</Callout> : null}
      {providers.data?.github || providers.data?.google ? (
        <div className="mb-6 grid gap-2">
          {providers.data.github ? <Button variant="outline" onClick={() => void oauth("github")}>Continue with GitHub</Button> : null}
          {providers.data.google ? <Button variant="outline" onClick={() => void oauth("google")}>Continue with Google</Button> : null}
        </div>
      ) : null}
      <form onSubmit={submit} className="grid gap-4" noValidate>
        <Field id="email" label="Email" required>
          <Input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field id="password" label="Password" required>
          <Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        {captcha.widget}
        {error ? <p role="alert" className="text-ui text-danger-fg">{error}</p> : null}
        <Button type="submit" disabled={busy || !email || !password || !captcha.ready}>Sign in</Button>
        <AppLink href="/forgot-password" className="text-ui text-fg-secondary hover:text-fg-primary">Forgot password?</AppLink>
      </form>
    </AuthCard>
  );
}
